import type { IncomingPayload } from '@moojo/serving-box-api'
import type { HeaderRecord, SearchParamsRecord, Transfer } from '@moojo/serving-box-client'
import { TransferError } from '@moojo/serving-box-client'
import type { AxiosInstance, RawAxiosResponseHeaders } from 'axios'
import axios, { isAxiosError } from 'axios'
import { z } from 'zod'

const ApiError = z.object({
  errorMessage: z.string(),
  requestId: z.string(),
})

const StatusAndErrorResponse = z.object({
  status: z.string().min(1),
  traceId: z.string(),
  error: z.object({
    message: z.string(),
    code: z.string(),
  }),
})

const maybeParseErrorAndThrow = (input: unknown) => {
  const m1 = ApiError.safeParse(input)
  if (m1.success) {
    throw new TransferError(m1.data.requestId, m1.data.errorMessage)
  }

  const m2 = StatusAndErrorResponse.safeParse(input)
  if (m2.success) {
    throw new TransferError(m2.data.traceId, m2.data.error.message)
  }
}

const tryGetAmzRequestIdHeader = (headers: RawAxiosResponseHeaders | undefined) => {
  const amzReqId = headers?.['x-amzn-requestid']
  return typeof amzReqId === 'string' ? amzReqId : undefined
}

function newClient() {
  return axios.create({
    headers: {
      'Content-Type': 'application/json',
    },
  })
}

async function makeCall(
  client: AxiosInstance,
  url: string,
  dataToSend: unknown,
  reqHeaders: HeaderRecord | undefined,
  searchParams: SearchParamsRecord | undefined,
) {
  try {
    const u = new URL(url)
    for (const [k, v] of Object.entries(searchParams ?? {})) {
      if (v !== undefined) {
        u.searchParams.set(k, v)
      }
    }
    const { data, status, headers } = await client.post(u.toString(), dataToSend, { headers: reqHeaders })
    if (status !== 200) {
      maybeParseErrorAndThrow(data)
      throw new TransferError(tryGetAmzRequestIdHeader(headers), `Unknown error (status code: ${status})`)
    }

    return data
  } catch (e) {
    if (isAxiosError(e)) {
      maybeParseErrorAndThrow(e.response?.data)
      throw new TransferError(tryGetAmzRequestIdHeader(e.response?.headers), `Call to ${url} failed: ${e.message}`)
    }
    throw new TransferError(undefined, `unknown error`)
  }
}

// TODO:
// better error handling
// api keys
export function createApiTransfer(url: string): Transfer {
  const client = newClient()
  return (payload: IncomingPayload) =>
    makeCall(
      client,
      url,
      { ...payload, headers: payload.headers, searchParams: payload.searchParams },
      payload.headers,
      payload.searchParams,
    )
}

// We do not use path.join() because this file can be bundled also into frontend code where node's path library is not
// available (and, at the moment, I do not want to polyfill it).
function appendPath(base: string, path: string): string {
  return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
}

/**
 * Creates a Transfer function that handles REST API calls to a specified base URL.
 *
 * @param baseUrl - The base URL for the REST API endpoints. This will be combined with endpoint names from the payload
 * to form complete URLs.
 *
 * @returns A Transfer function
 */
export function createRestTransfer(baseUrl: string): Transfer {
  const client = newClient()
  return (payload: IncomingPayload) =>
    makeCall(
      client,
      appendPath(baseUrl, payload.endpointName),
      payload.endpointRequest,
      payload.headers,
      payload.searchParams,
    )
}
